import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Input, Select, Table, Tag} from 'antd';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import {GET_COMPLY_CUBE_CLIENTS} from '../resolvers';
import {
  buildClientsInput,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_CLIENTS_KEY,
} from '../kycComplyCubeUtils';
import {
  ComplyCubeTablePagination,
  ComplyCubeUserCell,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';
import moment from 'moment';

const {Option} = Select;

export function ComplyCubeClientsTab({openClientDetail}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchDraft, setSearchDraft] = useState('');
  const [nationalityDraft, setNationalityDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedCountryCode, setAppliedCountryCode] = useState('');

  const {data: countriesData, loading: countriesLoading} = useQuery(
    GET_ALL_COUNTRIES,
    {
      errorPolicy: 'ignore',
    },
  );

  const countries = useMemo(
    () =>
      countriesData?.getAllCountries?.filter(
        c => c?.__typename !== 'Error' && c?.countryCode,
      ) || [],
    [countriesData?.getAllCountries],
  );

  const showReset =
    searchDraft.trim() !== '' ||
    nationalityDraft !== '' ||
    appliedSearch.trim() !== '' ||
    appliedCountryCode !== '';

  const handleApply = () => {
    setAppliedSearch(searchDraft);
    setAppliedCountryCode(nationalityDraft || '');
    setPage(1);
  };

  const handleReset = () => {
    setSearchDraft('');
    setNationalityDraft('');
    setAppliedSearch('');
    setAppliedCountryCode('');
    setPage(1);
  };

  const variables = useMemo(
    () => ({
      input: buildClientsInput(page, limit, {
        search: appliedSearch,
        nationality: appliedCountryCode,
      }),
    }),
    [page, limit, appliedSearch, appliedCountryCode],
  );

  const {data, loading} = useQuery(GET_COMPLY_CUBE_CLIENTS, {
    variables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const {error, rows, total} = unwrapListResponse(
    data,
    COMPLY_CUBE_CLIENTS_KEY,
    'ResponseWithComplyCubeClients',
  );

  return (
    <>
      {error ? (
        <Alert
          type="error"
          message={error.message || 'Request failed'}
          showIcon
        />
      ) : null}
      <div className={styles.viewerFilters}>
        <Input
          allowClear
          placeholder="Search entity, email…"
          className={styles.viewerSearch}
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <Select
          allowClear
          showSearch
          placeholder="Nationality"
          style={{width: 250}}
          className={styles.viewerFilterInput}
          value={nationalityDraft || undefined}
          onChange={v => setNationalityDraft(v || '')}
          loading={countriesLoading}
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }>
          {countries.map(c => (
            <Option
              key={c.id || c.countryCode}
              value={c.countryCode}
              label={c.name || c.countryCode}>
              {c.name || c.countryCode}
            </Option>
          ))}
        </Select>
        <div className={styles.viewerFilterActions}>
          <Button type="primary" onClick={handleApply}>
            Submit
          </Button>
          {showReset ? <Button onClick={handleReset}>Reset</Button> : null}
        </div>
      </div>
      <Table
        className="antd-table"
        loading={loading}
        pagination={false}
        rowKey={r => r.id || r._id}
        dataSource={rows}
        onRow={record => ({
          onClick: () => openClientDetail(record.id || record._id),
          className: styles.clickableRow,
        })}
        columns={[
          {
            title: 'User',
            key: 'user',
            width: 228,
            render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
          },

          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (_, r) => <Tag>{r?.type?.toUpperCase() || '-'}</Tag>,
          },
          {
            title: 'Nationality',
            key: 'nationality',
            render: (_, r) => r?.personDetails?.nationality || '-',
          },
          {
            title: 'DOB',
            key: 'dob',
            render: (_, r) =>
              r?.personDetails?.dob
                ? moment(r?.personDetails?.dob).format('DD MMM YYYY')
                : '-',
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
          },
        ]}
      />
      <ComplyCubeTablePagination
        page={page}
        limit={limit}
        total={total}
        rowCount={rows.length}
        loading={loading}
        onPageChange={(nextPage, nextLimit) => {
          setLimit(nextLimit);
          setPage(nextPage);
        }}
        itemName="clients"
      />
    </>
  );
}
